import * as React from "react";

/**
 * Controlled/uncontrolled state, matching `@salt-ds/core`'s `useControlled`.
 *
 * ── THE READ AND THE WRITE USE DIFFERENT RULES, AND THAT IS THE WHOLE POINT ───────────
 *
 * The VALUE is resolved fresh on every render: if `controlled` is defined right now, it wins.
 * Whether the component may keep its OWN state is decided ONCE, on the first render, and
 * pinned in a ref.
 *
 * Reading that as one rule instead of two is a real bug, and this hook shipped with it. Pinning
 * the READ as well means a `value` that arrives later — the ordinary case of a form rendering
 * before its data does — is ignored forever, and the control silently stops reflecting its own
 * prop. `src/components/ui/switch.test.tsx` renders a Switch uncontrolled and then rerenders it
 * with `checked`, and that is what caught it.
 *
 * The pinned ref still matters: it stops a component that STARTED controlled from also keeping
 * a private copy of the value, which is how a controlled input drifts from its owner's state.
 */
export function useControlled<T>({
  controlled,
  default: defaultValue,
}: {
  controlled: T | undefined;
  default: T;
}): [T, (value: T) => void] {
  const { current: isControlled } = React.useRef(controlled !== undefined);
  const [state, setState] = React.useState<T>(defaultValue);

  const setValueIfUncontrolled = React.useCallback(
    (next: T) => {
      if (!isControlled) setState(next);
    },
    [isControlled],
  );

  // Resolved per render, deliberately NOT from `isControlled`.
  const value = controlled !== undefined ? controlled : state;

  return [value, setValueIfUncontrolled];
}
